// drkDarkTheme.js
import { createTheme } from '@mui/material/styles';

// Hauptfarbpalette für das DRK-Design im Dark Mode
// Angepasst an Logo-Farbverlauf für konsistentes Branding
const drkRed = '#f28424'; // Mittelwert des Logo-Farbverlaufs (warmes Orange)
const drkLightRed = 'rgba(242, 132, 36, 0.12)'; // Basierend auf Logo-Farbe mit reduzierter Opacity

// MUI-Blau Palette (für App-Blue)
const muiBlue = '#42a5f5'; // Aufgehellt für Dark Mode
const muiLightBlue = '#64b5f6';
const muiDarkBlue = '#1976d2';

// Kommunikationskanal-Farben (aufgehellt für Dark Mode)
const channelColors = {
    email: {
        main: '#64b5f6', // Heller als Light Mode
        light: 'rgba(100, 181, 246, 0.15)',
        dark: '#42a5f5',
        contrastText: '#fff',
    },
    sms: {
        main: '#ffb74d', // Orange bleibt ähnlich
        light: 'rgba(255, 183, 77, 0.15)',
        dark: '#ffa726',
        contrastText: '#000',
    },
    inapp: {
        main: '#81c784', // Helleres Grün
        light: 'rgba(129, 199, 132, 0.15)',
        dark: '#66bb6a',
        contrastText: '#000',
    },
    push: {
        main: '#ba68c8', // Lila bleibt ähnlich
        light: 'rgba(186, 104, 200, 0.15)',
        dark: '#ab47bc',
        contrastText: '#fff',
    },
};

const drkDarkTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: drkRed,
            light: '#f9ae1f',  // Start des Logo-Verlaufs (goldenes Orange)
            dark: '#eb5b28',   // Ende des Logo-Verlaufs (rötliches Orange)
            contrastText: '#fff',
        },
        secondary: {
            main: '#81C784', // Helleres Grün für Dark Mode
            light: 'rgba(129, 199, 132, 0.15)',
            dark: '#66BB6A',
            contrastText: '#000',
        },
        error: {
            main: '#F44336', // Standard MUI Error
            light: '#E57373',
            dark: '#D32F2F',
        },
        warning: {
            main: '#FF9800',
            light: '#FFB74D',
            dark: '#F57C00',
        },
        info: {
            main: '#42A5F5',
            light: '#64B5F6',
            dark: '#1976D2',
        },
        success: {
            main: '#66BB6A',
            light: '#81C784',
            dark: '#4CAF50',
        },
        background: {
            default: '#121212', // Material Design Dark Theme Standard
            paper: '#1E1E1E', // Erhöhte Surfaces
            accent: drkLightRed, // Für DRK-spezifische Akzente
            muted: '#2C2C2C', // Für sekundäre Surfaces
        },
        text: {
            primary: 'rgba(255, 255, 255, 0.87)', // Hoher Kontrast
            secondary: 'rgba(255, 255, 255, 0.6)', // Mittlerer Kontrast
            muted: 'rgba(255, 255, 255, 0.38)', // Niedriger Kontrast
            disabled: 'rgba(255, 255, 255, 0.38)',
        },
        divider: 'rgba(255, 255, 255, 0.12)',
        action: {
            active: 'rgba(255, 255, 255, 0.56)',
            hover: 'rgba(255, 255, 255, 0.06)',
            selected: 'rgba(255, 255, 255, 0.12)',
            disabled: 'rgba(255, 255, 255, 0.26)',
            disabledBackground: 'rgba(255, 255, 255, 0.12)',
        },
        appBlue: {
            main: muiBlue,
            light: muiLightBlue,
            dark: muiDarkBlue,
            contrastText: '#fff',
        },
        // Challenge Card Farben für Planungs-Score-Kategorien (Dark Mode optimiert)
        challengeCard: {
            success: {
                background: '#1E3A1F',              // Dunkles Waldgrün
                border: '#66BB6A',                  // Success.main
                headerText: '#81C784',              // Success.light
                headerBackplate: '#2C2C2C',         // background.muted
                bodyText: 'rgba(255,255,255,0.87)'  // text.primary
            },
            info: {
                background: '#1A354A',              // Dunkles Marineblau
                border: '#64B5F6',                  // Info.light
                headerText: '#64B5F6',              // Info.light
                headerBackplate: '#2C2C2C',
                bodyText: 'rgba(255,255,255,0.87)'
            },
            warning: {
                background: '#3D2A1A',              // Dunkles warmes Orange-Braun
                border: '#FF9800',                  // Warning.main
                headerText: '#FFB74D',              // Warning.light
                headerBackplate: '#2C2C2C',
                bodyText: 'rgba(255,255,255,0.87)'
            },
            error: {
                background: '#3D1E1C',              // Dunkles Weinrot
                border: '#F44336',                  // Error.main
                headerText: '#E57373',              // Error.light
                headerBackplate: '#2C2C2C',
                bodyText: 'rgba(255,255,255,0.87)'
            }
        },
        // Kommunikationskanal-Farben
        communicationChannels: channelColors,
    },
    typography: {
        fontFamily: [
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
        ].join(','),
        h1: {
            fontWeight: 700,
            color: 'rgba(255, 255, 255, 0.87)',
        },
        h2: {
            fontWeight: 700,
            color: 'rgba(255, 255, 255, 0.87)',
        },
        h3: {
            fontWeight: 600,
            color: 'rgba(255, 255, 255, 0.87)',
        },
        h4: {
            fontWeight: 600,
            color: drkRed, // DRK-Rot für Hauptüberschriften
        },
        h5: {
            fontWeight: 600,
            color: drkRed,
        },
        h6: {
            fontWeight: 500,
            color: 'rgba(255, 255, 255, 0.87)',
        },
        subtitle1: {
            fontWeight: 500,
        },
        subtitle2: {
            fontWeight: 500,
        },
        body1: {
            color: 'rgba(255, 255, 255, 0.87)',
        },
        body2: {
            color: 'rgba(255, 255, 255, 0.6)',
        },
        colorHeader: {
            fontWeight: 600,
            color: 'white',
            fontSize: '1.5rem',
        },
    },
    shape: {
        borderRadius: 8,
    },
    shadows: [
        'none',
        '0px 2px 4px rgba(0, 0, 0, 0.5)', // Stärkere Schatten für Dark Mode
        '0px 4px 8px rgba(0, 0, 0, 0.5)',
        '0px 6px 12px rgba(0, 0, 0, 0.5)',
        '0px 8px 16px rgba(0, 0, 0, 0.5)',
        '0px 10px 20px rgba(0, 0, 0, 0.5)',
        '0px 12px 24px rgba(0, 0, 0, 0.5)',
        '0px 14px 28px rgba(0, 0, 0, 0.5)',
        '0px 16px 32px rgba(0, 0, 0, 0.5)',
        '0px 18px 36px rgba(0, 0, 0, 0.5)',
        '0px 20px 40px rgba(0, 0, 0, 0.5)',
        '0px 22px 44px rgba(0, 0, 0, 0.5)',
        '0px 24px 48px rgba(0, 0, 0, 0.5)',
        '0px 26px 52px rgba(0, 0, 0, 0.5)',
        '0px 28px 56px rgba(0, 0, 0, 0.5)',
        '0px 30px 60px rgba(0, 0, 0, 0.5)',
        '0px 32px 64px rgba(0, 0, 0, 0.5)',
        '0px 34px 68px rgba(0, 0, 0, 0.5)',
        '0px 36px 72px rgba(0, 0, 0, 0.5)',
        '0px 38px 76px rgba(0, 0, 0, 0.5)',
        '0px 40px 80px rgba(0, 0, 0, 0.5)',
        '0px 42px 84px rgba(0, 0, 0, 0.5)',
        '0px 44px 88px rgba(0, 0, 0, 0.5)',
        '0px 46px 92px rgba(0, 0, 0, 0.5)',
    ],
    components: {
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    overflow: 'hidden',
                    backgroundColor: '#1E1E1E',
                    // Erhöhte Elevation für bessere Sichtbarkeit
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: 24,
                    '&:last-child': {
                        paddingBottom: 24,
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: 30,
                    padding: '6px 16px',
                    boxShadow: '0 4px 10px rgba(242, 132, 36, 0.25)',
                    '&:hover': {
                        boxShadow: '0 6px 15px rgba(242, 132, 36, 0.35)',
                    },
                },
                containedPrimary: {
                    backgroundColor: drkRed,
                    '&:hover': {
                        backgroundColor: '#eb5b28',  // Ende des Logo-Verlaufs
                    },
                },
                outlinedPrimary: {
                    borderColor: drkRed,
                    color: drkRed,
                    '&:hover': {
                        borderColor: '#eb5b28',
                        backgroundColor: 'rgba(242, 132, 36, 0.08)',
                    },
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 8,
                        backgroundColor: '#2C2C2C', // Dunkler für Inputs
                        '&:-webkit-autofill': {
                            WebkitBoxShadow: '0 0 0 1000px #2C2C2C inset',
                            WebkitTextFillColor: 'rgba(255, 255, 255, 0.87)',
                        },
                        '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.23)',
                        },
                        '&:hover fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.4)',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: drkRed,
                        },
                    },
                    '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.6)',
                        '&.Mui-focused': {
                            color: drkRed,
                        },
                    },
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                },
                icon: {
                    alignItems: 'center',
                },
                standardError: {
                    backgroundColor: 'rgba(244, 67, 54, 0.15)',
                    color: '#F44336',
                },
                standardWarning: {
                    backgroundColor: 'rgba(255, 152, 0, 0.15)',
                    color: '#FF9800',
                },
                standardInfo: {
                    backgroundColor: 'rgba(66, 165, 245, 0.15)',
                    color: '#42A5F5',
                },
                standardSuccess: {
                    backgroundColor: 'rgba(102, 187, 106, 0.15)',
                    color: '#66BB6A',
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    color: drkRed,
                    textDecoration: 'none',
                    fontWeight: 500,
                    '&:hover': {
                        textDecoration: 'underline',
                    },
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    margin: '16px 0',
                    borderColor: 'rgba(255, 255, 255, 0.12)',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                colorPrimary: {
                    backgroundColor: '#1E1E1E',
                    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))',
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 12,
                    overflow: 'hidden',
                    backgroundColor: '#1E1E1E',
                    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))',
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    backgroundColor: drkRed,
                    color: '#fff',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundColor: '#1E1E1E',
                    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))',
                },
                elevation1: {
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.5)',
                },
                elevation2: {
                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.5)',
                },
                elevation3: {
                    boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.5)',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
                    backgroundColor: '#121212',
                },
                head: {
                    backgroundColor: '#121212',
                    fontWeight: 600,
                    borderBottom: '2px solid rgba(255, 255, 255, 0.12)',
                    borderRight: '1px solid rgba(255, 255, 255, 0.12)',
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: '#2C2C2C',
                    color: 'rgba(255, 255, 255, 0.87)',
                    fontSize: '0.875rem',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    backgroundColor: '#2C2C2C',
                },
                colorPrimary: {
                    backgroundColor: drkRed,
                    color: '#fff',
                },
                colorSecondary: {
                    backgroundColor: '#81C784', // success.light
                    color: '#000',
                },
                colorSuccess: {
                    backgroundColor: '#81C784', // success.light
                    color: '#000',
                },
                colorWarning: {
                    backgroundColor: '#FFB74D', // warning.light
                    color: '#000',
                },
                colorInfo: {
                    backgroundColor: '#64B5F6', // info.light
                    color: '#000',
                },
                colorError: {
                    backgroundColor: '#E57373', // error.light
                    color: '#000',
                },
                colorDefault: {
                    backgroundColor: '#616161', // Mittleres Grau
                    color: '#fff',
                },
            },
        },
    },
});

export default drkDarkTheme;
